import { useEffect, useMemo, useState } from 'react';
import type { GameSocketApi } from './hooks/useGameSocket';
import { useMe, usePrivateState, useSnapshot } from './store/gameStore';
import Board from './components/Board';
import Dice from './components/Dice';
import Chat from './components/Chat';
import GameFeed from './components/GameFeed';
import { playWin } from './sound';
import { COLOR_LABELS } from '../../shared/board';
import type { Color } from '../../shared/board';

const MARBLES: Record<Color, string> = {
  red: '#ff5a5a',
  green: '#35d07f',
  yellow: '#ffce3d',
  blue: '#4aa8ff',
};

export default function Game({ socket }: { socket: GameSocketApi }) {
  const snapshot = useSnapshot();
  const me = useMe();
  const privateState = usePrivateState();

  useEffect(() => {
    if (snapshot?.phase === 'gameover') playWin();
  }, [snapshot?.phase]);

  if (!snapshot || !me) return null;

  const myColor = me.color;
  const myTurn = snapshot.currentColor === myColor && snapshot.phase === 'playing';
  const canRoll = !!privateState?.youCanRoll;
  const currentPlayer = snapshot.players.find((p) => p.color === snapshot.currentColor);
  const winner = snapshot.winnerId ? snapshot.players.find((p) => p.id === snapshot.winnerId) : null;

  const onRoll = () => {
    if (canRoll) socket.rollDice();
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-gradient-to-b from-[#2a1848] via-snk-bg to-[#140a24]">
      <ArenaBackdrop currentColor={myTurn ? myColor : null} />

      <div className="relative z-10 mx-auto w-full max-w-[1560px] px-4 py-6 pb-24 sm:px-6">
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3 pl-28">
          <span className="snk-glass inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-semibold text-snk-ink">
            <span className="text-[10px] font-bold uppercase tracking-widest text-snk-muted">Room</span>
            <span className="font-mono font-bold tracking-[0.18em] text-[#ffd98a]">{snapshot.roomCode}</span>
          </span>

          {snapshot.phase === 'gameover' ? (
            <span className="snk-glass inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-bold text-[#ffd98a]">
              Game over
            </span>
          ) : myTurn ? (
            <span className="snk-gradient inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#35d07f] via-[#ffd257] to-[#ff8a3d] px-4 py-1.5 text-sm font-bold text-[#1a1020] shadow-glow">
              <span className="relative flex h-2.5 w-2.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white/70" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-white" />
              </span>
              {canRoll ? "It's your turn — roll!" : 'Moving…'}
            </span>
          ) : (
            <span className="snk-glass inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-bold text-snk-ink">
              <span
                className="h-3 w-3 rounded-full"
                style={{ backgroundColor: currentPlayer ? MARBLES[currentPlayer.color] : '#888' }}
              />
              {currentPlayer
                ? `${currentPlayer.isBot ? '🤖 ' : ''}${currentPlayer.name} is moving…`
                : 'Waiting…'}
            </span>
          )}
        </div>

        <div className="grid gap-6 lg:grid-cols-[290px_minmax(0,1fr)_330px]">
          <div className="order-2 lg:order-none">
            <div className="snk-glass rounded-3xl p-4">
              <p className="mb-3 px-1 text-xs font-bold uppercase tracking-widest text-snk-muted">Players</p>
              <ul className="space-y-2">
                {snapshot.players.map((p) => {
                  const isTurn = snapshot.phase === 'playing' && p.color === snapshot.currentColor;
                  const isBot = !!p.isBot;
                  return (
                    <li
                      key={p.id}
                      className={`flex items-center gap-2.5 rounded-2xl px-3 py-2.5 transition-all duration-300 ${
                        isTurn
                          ? 'bg-white/[0.09] ring-1 ring-white/25 shadow-[0_0_24px_-8px_rgba(255,255,255,0.4)]'
                          : 'bg-white/[0.04]'
                      }`}
                    >
                      <span
                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 border-white/30 text-xs font-bold text-white"
                        style={{
                          background: `radial-gradient(circle at 32% 28%, rgba(255,255,255,0.85) 0%, ${MARBLES[p.color]} 45%, rgba(0,0,0,0.3) 130%)`,
                        }}
                      >
                        {isBot ? '🤖' : p.name.slice(0, 1).toUpperCase()}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-bold text-snk-ink">
                          {p.name} {p.id === me.id && <span className="text-snk-muted/70">(you)</span>}
                          {isBot && (
                            <span className="ml-1.5 rounded-full bg-white/10 px-1.5 py-px text-[10px] font-bold uppercase text-snk-muted">
                              {p.botDifficulty ?? 'bot'}
                            </span>
                          )}
                        </p>
                        <p className="text-[11px] font-bold text-snk-muted">
                          {p.position === 0 ? 'Start' : `Square ${p.position}`}
                        </p>
                      </div>
                      {p.rank && (
                        <span className="shrink-0 rounded-full bg-gradient-to-br from-[#ffd257] to-[#ff9a4a] px-2 py-0.5 text-[10px] font-bold text-white shadow">
                          #{p.rank}
                        </span>
                      )}
                      <span
                        className={`h-2 w-2 shrink-0 rounded-full ${p.isBot || p.connected ? 'bg-emerald-400' : 'bg-white/20'}`}
                        title={p.isBot ? 'bot' : p.connected ? 'connected' : 'disconnected'}
                      />
                    </li>
                  );
                })}
              </ul>
              <p className="mt-3 px-1 text-[11px] leading-snug text-snk-muted/80">
                You are{' '}
                <span className="font-bold" style={{ color: MARBLES[myColor] }}>
                  {COLOR_LABELS[myColor]}
                </span>{' '}
                — land exactly on 100 to win.
              </p>
            </div>
          </div>

          <div className="relative flex order-1 items-center justify-center lg:order-none">
            <div
              className="pointer-events-none absolute inset-0 opacity-60 transition-opacity duration-700"
              style={{
                background:
                  myTurn && canRoll
                    ? 'radial-gradient(circle at 50% 45%, rgba(255,201,77,0.16) 0%, transparent 62%)'
                    : 'radial-gradient(circle at 50% 45%, rgba(255,255,255,0.05) 0%, transparent 62%)',
                filter: 'blur(6px)',
              }}
              aria-hidden="true"
            />
            <div className="aspect-square w-full max-w-[calc(100dvh-168px)]">
              <Board
                players={snapshot.players}
                myColor={myColor}
                currentColor={snapshot.currentColor}
                lastRoll={snapshot.lastRoll}
              />
            </div>
          </div>

          <aside className="order-3 flex flex-col gap-4 lg:order-none">
            <div className="snk-glass relative overflow-hidden rounded-3xl p-5">
              <div className="pointer-events-none absolute -right-10 -top-14 h-36 w-36 rounded-full bg-[#35d07f]/15 blur-2xl" aria-hidden="true" />
              <div className="relative flex items-center gap-4 pb-9">
                <Dice
                  value={snapshot.lastRoll?.value ?? snapshot.dice}
                  rollId={snapshot.lastRoll?.id}
                  canRoll={canRoll}
                  onRoll={onRoll}
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate font-display text-lg font-bold text-snk-ink">
                    {myTurn ? (
                      canRoll ? (
                        <span className="snk-glow-pulse bg-gradient-to-r from-[#ffe58a] to-[#8ce99a] bg-clip-text text-transparent">
                          Your turn — roll!
                        </span>
                      ) : (
                        'Your turn'
                      )
                    ) : (
                      <>
                        <span
                          className="mr-1.5 inline-block h-4 w-4 rounded-full align-[-2px]"
                          style={{ backgroundColor: currentPlayer ? MARBLES[currentPlayer.color] : '#888' }}
                        />
                        {currentPlayer ? `${currentPlayer.isBot ? '🤖 ' : ''}${currentPlayer.name}` : '…'}
                        <span className="text-snk-muted">&apos;s turn</span>
                      </>
                    )}
                  </p>
                  <p className="mt-1 text-[13px] leading-snug text-snk-muted">
                    {hintText({ myTurn, canRoll, lastRoll: snapshot.lastRoll, winner: winner ?? null, passPending: snapshot.passPending })}
                  </p>
                </div>
              </div>

              {snapshot.phase === 'playing' && snapshot.turnDeadlineAt != null && (
                <TurnTimer snapshot={snapshot} />
              )}

              {snapshot.extraTurn && snapshot.phase === 'playing' && (
                <p className="snk-pop relative mt-3 rounded-xl bg-[#ffce3d]/15 px-3 py-2 text-center text-xs font-bold text-[#ffe58a] ring-1 ring-[#ffce3d]/30">
                  Six! Roll again — extra turn.
                </p>
              )}
              {snapshot.lastRoll?.teleport === 'ladder' && (
                <p className="snk-pop relative mt-3 rounded-xl bg-[#35d07f]/15 px-3 py-2 text-center text-xs font-bold text-[#8ce99a] ring-1 ring-[#35d07f]/30">
                  Ladder! Climbed to {snapshot.lastRoll.to}.
                </p>
              )}
              {snapshot.lastRoll?.teleport === 'snake' && (
                <p className="snk-pop relative mt-3 rounded-xl bg-red-500/15 px-3 py-2 text-center text-xs font-bold text-red-300 ring-1 ring-red-400/30">
                  Snake! Slid to {snapshot.lastRoll.to}.
                </p>
              )}
              {snapshot.lastRoll?.overshot && (
                <p className="snk-pop relative mt-3 rounded-xl bg-white/10 px-3 py-2 text-center text-xs font-bold text-snk-muted ring-1 ring-white/15">
                  Need an exact roll to finish.
                </p>
              )}
              {snapshot.lastRoll?.forfeited && (
                <p className="snk-pop relative mt-3 rounded-xl bg-red-500/15 px-3 py-2 text-center text-xs font-bold text-red-300 ring-1 ring-red-400/30">
                  Three sixes — turn lost!
                </p>
              )}
            </div>

            <GameFeed entries={snapshot.chat} />
            <Chat entries={snapshot.chat} onSend={(t) => socket.sendChat(t)} />
          </aside>
        </div>
      </div>

      {snapshot.phase === 'gameover' && winner && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden bg-black/70 px-6 backdrop-blur-md">
          <Celebration />
          <div className="relative w-full max-w-sm rounded-[28px] border border-white/15 bg-gradient-to-b from-[#351e5c] to-[#140a24] p-8 text-center shadow-soft">
            <div className="snk-trophy mx-auto flex h-24 w-24 items-center justify-center rounded-full bg-gradient-to-br from-[#ffd257]/30 to-[#35d07f]/30 ring-2 ring-[#ffc94d]/50 shadow-glow-gold">
              <span className="text-6xl">🏆</span>
            </div>
            <h2 className="mt-4 bg-gradient-to-br from-[#ffe9a8] via-[#ffc94d] to-[#8ce99a] bg-clip-text font-display text-4xl font-bold text-transparent">
              {winner.name} wins!
            </h2>
            <p className="mt-2 text-sm text-snk-muted">
              <span className="font-bold" style={{ color: MARBLES[winner.color] }}>
                {COLOR_LABELS[winner.color]}
              </span>{' '}
              landed on 100 first.
            </p>

            <div className="mt-6 space-y-1.5 text-left">
              {snapshot.players
                .filter((p) => p.rank)
                .sort((a, b) => (a.rank ?? 0) - (b.rank ?? 0))
                .map((p, i) => (
                  <div key={p.id} className="flex items-center justify-between rounded-xl bg-white/[0.06] px-3 py-2.5">
                    <span className="flex items-center gap-2.5 font-semibold text-snk-ink">
                      <span className="text-sm">{['🥇', '🥈', '🥉', '4️⃣'][i] ?? '🎖️'}</span>
                      <span className="h-4 w-4 rounded-full" style={{ backgroundColor: MARBLES[p.color] }} />
                      {p.name}
                    </span>
                    <span className="text-xs font-bold text-snk-muted">#{p.rank}</span>
                  </div>
                ))}
            </div>

            <button
              onClick={() => socket.leave()}
              className="snk-gradient mt-7 w-full rounded-xl bg-gradient-to-r from-[#35d07f] via-[#ffd257] to-[#ff8a3d] py-3.5 text-sm font-bold tracking-wide text-[#1a1020] shadow-glow transition hover:brightness-110 active:scale-[0.98]"
            >
              Play again
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function TurnTimer({ snapshot }: { snapshot: NonNullable<ReturnType<typeof useSnapshot>> }) {
  const [now, setNow] = useState(() => Date.now());
  const deadline = snapshot.turnDeadlineAt;
  const playing = snapshot.phase === 'playing';

  useEffect(() => {
    if (!playing || deadline == null) return;
    setNow(Date.now());
    const id = window.setInterval(() => setNow(Date.now()), 200);
    return () => window.clearInterval(id);
  }, [playing, deadline]);

  if (!playing || deadline == null) return null;

  const total = snapshot.turnTimeoutMs || 1;
  const remaining = Math.max(0, deadline - now);
  const pct = Math.max(0, Math.min(1, remaining / total));
  const secs = Math.ceil(remaining / 1000);
  const low = remaining <= 10_000;

  return (
    <div className="relative mt-4">
      <div className="mb-1 flex items-center justify-between">
        <span className="text-[10px] font-bold uppercase tracking-widest text-snk-muted">Turn timer</span>
        <span className={`font-mono text-xs font-bold ${low ? 'text-red-300' : 'text-snk-ink'}`}>{secs}s</span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-white/10">
        <div
          className={`h-full rounded-full transition-[width] duration-200 ease-linear ${
            low ? 'bg-gradient-to-r from-red-500 to-orange-400' : 'bg-gradient-to-r from-[#ffd257] to-[#35d07f]'
          }`}
          style={{ width: `${pct * 100}%` }}
        />
      </div>
    </div>
  );
}

function ArenaBackdrop({ currentColor }: { currentColor: Color | null }) {
  const base = currentColor ? MARBLES[currentColor] : null;
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
      <div
        className="absolute inset-0 opacity-[0.35]"
        style={{
          backgroundImage: 'radial-gradient(rgba(255,255,255,0.14) 1px, transparent 1px)',
          backgroundSize: '26px 26px',
        }}
      />
      <div
        className="absolute -top-32 -left-24 h-96 w-96 rounded-full blur-3xl transition-colors duration-700"
        style={{ backgroundColor: base ? `${base}22` : 'rgba(53,208,127,0.12)' }}
      />
      <div
        className="absolute top-1/3 -right-28 h-96 w-96 rounded-full blur-3xl transition-colors duration-700"
        style={{ backgroundColor: base ? `${base}1c` : 'rgba(255,138,61,0.12)' }}
      />
      <div className="absolute -bottom-24 left-1/4 h-80 w-80 rounded-full bg-[#ffce3d]/10 blur-3xl" />
      <div className="absolute bottom-1/3 -left-20 h-72 w-72 rounded-full bg-[#4aa8ff]/10 blur-3xl" />
    </div>
  );
}

function Celebration() {
  const pieces = useMemo(
    () =>
      Array.from({ length: 36 }, (_, i) => ({
        id: i,
        left: Math.random() * 100,
        delay: Math.random() * 2.4,
        duration: 3.2 + Math.random() * 2.6,
        size: 7 + Math.random() * 8,
        color: ['#ff5a5a', '#35d07f', '#ffce3d', '#4aa8ff', '#ffc94d', '#ff8a3d'][i % 6],
        round: Math.random() > 0.5,
      })),
    [],
  );
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
      {pieces.map((p) => (
        <span
          key={p.id}
          className="snk-confetti"
          style={{
            left: `${p.left}%`,
            width: p.size,
            height: p.round ? p.size : p.size * 0.55,
            backgroundColor: p.color,
            borderRadius: p.round ? '50%' : '3px',
            animationDuration: `${p.duration}s`,
            animationDelay: `${p.delay}s`,
          }}
        />
      ))}
    </div>
  );
}

function hintText({
  myTurn,
  canRoll,
  lastRoll,
  winner,
  passPending,
}: {
  myTurn: boolean;
  canRoll: boolean;
  lastRoll: NonNullable<ReturnType<typeof useSnapshot>>['lastRoll'];
  winner: { name: string } | null;
  passPending: boolean;
}) {
  if (winner) return `Game over — ${winner.name} won!`;
  if (myTurn && passPending) return 'Three sixes — passing…';
  if (lastRoll?.teleport === 'ladder') return `Climbed a ladder to ${lastRoll.to}.`;
  if (lastRoll?.teleport === 'snake') return `Slid down a snake to ${lastRoll.to}.`;
  if (lastRoll?.overshot) return 'Too far — stay put until you roll the exact finish.';
  if (myTurn && canRoll) return 'Tap the die to roll.';
  return 'Waiting for the other players…';
}
